import React, { useState, useRef } from 'react';
import { sans, mono, green, greenLight, greenDark, red, redLight, redDark, amber, amberLight, amberDark, useTheme } from '../tokens';
import { getDeadlineStatus, formatTimestamp } from '../helpers';

const teal = '#0D9488';
const tealLight = '#CCFBF1';
const purple = '#7C3AED';
const purpleLight = '#EDE9FE';
const purpleDark = '#5B21B6';

const BLOCK_REASONS = [
  'Waiting for external documents',
  'Pending prior task completion',
  'System access issue',
  'Waiting for management approval',
  'Quarterly filing — not due yet',
  'Vendor / third-party delay',
  'Under investigation',
  'Other',
];

function TaskItem({ task, onToggleComplete, onToggleReview, onToggleBlocked, onUpdateDeadline, mockToday, userInitials, t, expanded, onToggleExpand }) {
  const dlStatus = task.blocked ? 'blocked' : getDeadlineStatus(task.dueDate, task.done, mockToday);
  const isOverdue = dlStatus === 'overdue';
  const isDueSoon = dlStatus === 'due-soon';
  const [showBlockDropdown, setShowBlockDropdown] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const canComplete = !task.blocked;
  const canReview = task.done && !task.reviewed && task.completedBy !== userInitials;

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    // File drop handling - for now just visual feedback
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      void('Files dropped on task', task.id, files.map(f => f.name));
      // TODO: actual file upload to Supabase storage
    }
  };

  return (
    <div style={{
      border: `1px solid ${task.reviewed ? green + '30' : task.blocked ? purple + '30' : t.border}`,
      borderRadius: 8,
      marginBottom: 6,
      overflow: 'hidden',
      background: dragOver ? tealLight + '40' : task.reviewed ? green + '06' : t.cardBg,
      transition: 'background 0.15s, border-color 0.15s',
    }}
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      {/* Task row — always visible */}
      <div
        onClick={onToggleExpand}
        style={{
          display: 'grid',
          gridTemplateColumns: '24px 1fr 60px 32px 32px',
          alignItems: 'center',
          padding: '10px 12px',
          cursor: 'pointer',
          gap: 8,
        }}
      >
        {/* Prep checkbox */}
        <div onClick={e => { e.stopPropagation(); if (canComplete) onToggleComplete(task.id); }}>
          {task.done ? (
            <div style={{
              width: 18, height: 18, borderRadius: 4, background: teal,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>✓</span>
            </div>
          ) : (
            <div style={{
              width: 18, height: 18, borderRadius: 4,
              border: `1.5px solid ${task.blocked ? purple + '60' : t.border}`,
              cursor: canComplete ? 'pointer' : 'not-allowed',
            }} />
          )}
        </div>

        {/* Task name */}
        <div>
          <div style={{
            fontSize: 12, fontFamily: sans, fontWeight: 500,
            color: task.reviewed ? t.textMuted : t.textPrimary,
            textDecoration: task.reviewed ? 'line-through' : 'none',
          }}>
            {task.name}
          </div>
          {task.completedBy && (
            <div style={{ fontSize: 9, fontFamily: mono, color: t.textFaint, marginTop: 1 }}>
              Prep: {task.completedBy}
              {task.reviewedBy && ` · Rev: ${task.reviewedBy}`}
            </div>
          )}
        </div>

        {/* Due date */}
        <span style={{
          fontSize: 10, fontFamily: mono, textAlign: 'right',
          color: isOverdue ? red : isDueSoon ? amber : t.textFaint,
          fontWeight: isOverdue ? 600 : 400,
        }}>
          {task.dueDate ? new Date(task.dueDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
        </span>

        {/* Review indicator */}
        <div style={{ textAlign: 'center' }} onClick={e => { e.stopPropagation(); if (canReview) onToggleReview(task.id); }}>
          {task.reviewed ? (
            <div style={{
              width: 18, height: 18, borderRadius: 4, background: green,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>✓</span>
            </div>
          ) : task.done ? (
            <div style={{
              width: 18, height: 18, borderRadius: 4,
              border: `1.5px solid ${canReview ? teal : t.border}`,
              cursor: canReview ? 'pointer' : 'not-allowed',
              opacity: canReview ? 1 : 0.4,
            }} />
          ) : (
            <span style={{ fontSize: 10, color: t.textFaint }}>—</span>
          )}
        </div>

        {/* Expand chevron */}
        <span style={{
          fontSize: 12, color: t.textFaint, textAlign: 'center',
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s', display: 'inline-block',
        }}>▾</span>
      </div>

      {/* Status badges row */}
      {(isOverdue || isDueSoon || task.blocked) && !expanded && (
        <div style={{ padding: '0 12px 8px', display: 'flex', gap: 4 }}>
          {task.blocked && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 8, background: purpleLight, color: purpleDark, fontWeight: 600 }}>Blocked</span>}
          {isOverdue && !task.blocked && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 8, background: redLight, color: redDark, fontWeight: 600 }}>Overdue</span>}
          {isDueSoon && !task.blocked && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 8, background: amberLight, color: amberDark, fontWeight: 600 }}>Due soon</span>}
        </div>
      )}

      {/* Expanded details */}
      {expanded && (
        <div style={{ padding: '0 12px 14px', borderTop: `1px solid ${t.border}`, marginTop: 0, paddingTop: 12 }}>

          {/* Description */}
          {task.description && (
            <div style={{
              fontSize: 11, lineHeight: 1.5, color: t.textSecondary,
              padding: '8px 10px', background: t.bgSurface, borderRadius: 6,
              marginBottom: 10, border: `1px solid ${t.borderLight || t.border}`,
            }}>
              {task.description}
            </div>
          )}

          {/* Deadline editor */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Deadline</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="date"
                value={task.dueDate || ''}
                onChange={e => onUpdateDeadline(task.id, e.target.value)}
                onClick={e => e.stopPropagation()}
                style={{
                  fontFamily: mono, fontSize: 11, color: t.textPrimary,
                  background: 'transparent', border: `1px solid ${t.border}`,
                  borderRadius: 4, padding: '3px 6px',
                }}
              />
              {isOverdue && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 8, background: redLight, color: redDark, fontWeight: 600 }}>Overdue</span>}
              {isDueSoon && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 8, background: amberLight, color: amberDark, fontWeight: 600 }}>Due soon</span>}
              {task.blocked && <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 8, background: purpleLight, color: purpleDark, fontWeight: 600 }}>Blocked</span>}
            </div>
          </div>

          {/* GL Accounts */}
          {task.accounts && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>GL Accounts</div>
              <div style={{ fontSize: 11, fontFamily: mono, color: t.textSecondary }}>{task.accounts}</div>
            </div>
          )}

          {/* Documents */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Documents</div>
            {task.documents && task.documents.length > 0 ? (
              task.documents.map((doc, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px',
                  border: `1px solid ${t.border}`, borderRadius: 4, marginBottom: 3, fontSize: 11,
                }}>
                  <span>📄</span>
                  <span style={{ flex: 1, color: t.textPrimary }}>{doc}</span>
                </div>
              ))
            ) : null}
            <div
              style={{
                marginTop: 4, padding: '12px', borderRadius: 6,
                border: `2px dashed ${dragOver ? teal : t.border}`,
                textAlign: 'center', fontSize: 11, color: t.textFaint,
                background: dragOver ? tealLight + '30' : 'transparent',
                transition: 'all 0.15s',
              }}
            >
              📎 Drop files here or click to upload
            </div>
          </div>

          {/* Workbook */}
          {task.workbook && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Linked Workbook</div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
                border: `1px solid ${greenLight}`, background: greenLight + '20', borderRadius: 6,
              }}>
                <span>📊</span>
                <span style={{ fontSize: 11, color: greenDark, fontWeight: 500 }}>{task.workbook}</span>
              </div>
            </div>
          )}

          {/* Block toggle */}
          <div style={{ position: 'relative' }}>
            {task.blocked ? (
              <button onClick={e => { e.stopPropagation(); onToggleBlocked(task.id, false, null); }} style={{
                border: `1px solid ${purpleLight}`, background: purpleLight + '30', borderRadius: 4,
                padding: '4px 10px', fontSize: 10, color: purple, cursor: 'pointer', fontFamily: sans,
              }}>
                Unblock{task.blockedReason ? ` (${task.blockedReason})` : ''}
              </button>
            ) : (
              <>
                <button onClick={e => { e.stopPropagation(); setShowBlockDropdown(!showBlockDropdown); }} style={{
                  border: `1px dashed ${t.border}`, background: 'transparent', borderRadius: 4,
                  padding: '4px 10px', fontSize: 10, color: t.textMuted, cursor: 'pointer', fontFamily: sans,
                }}>
                  Mark as blocked...
                </button>
                {showBlockDropdown && (
                  <div style={{
                    position: 'absolute', bottom: '100%', left: 0, marginBottom: 4,
                    border: `1px solid ${t.border}`, borderRadius: 6,
                    background: t.bgCard || '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    zIndex: 10, minWidth: 240, overflow: 'hidden',
                  }}>
                    {BLOCK_REASONS.map(reason => (
                      <button key={reason} onClick={e => { e.stopPropagation(); onToggleBlocked(task.id, true, reason); setShowBlockDropdown(false); }} style={{
                        display: 'block', width: '100%', textAlign: 'left',
                        padding: '7px 12px', border: 'none', cursor: 'pointer',
                        fontSize: 11, fontFamily: sans,
                        background: 'transparent', color: t.textPrimary,
                      }}>
                        {reason}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SectionPanel({ fsSection, tasks, onClose, onToggleComplete, onToggleReview, onToggleBlocked, onUpdateDeadline, mockToday, userInitials }) {
  const t = useTheme();
  const [expandedTask, setExpandedTask] = useState(null);

  const done = tasks.filter(t => t.done).length;
  const reviewed = tasks.filter(t => t.reviewed).length;
  const blocked = tasks.filter(t => t.blocked).length;
  const total = tasks.length;

  // Group tasks by sub-section
  const subSections = [];
  const seen = new Set();
  tasks.forEach(task => {
    if (!seen.has(task.section)) {
      seen.add(task.section);
      subSections.push(task.section);
    }
  });

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: 480,
      background: t.bgCard || '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
      zIndex: 100, display: 'flex', flexDirection: 'column', fontFamily: sans,
    }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${t.border}` }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
          <span style={{ fontSize: 24 }}>{fsSection.icon}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary }}>{fsSection.label}</div>
            <div style={{ fontSize: 10, fontFamily: mono, color: t.textFaint, marginTop: 2 }}>GL {fsSection.accounts}</div>
          </div>
          <button onClick={onClose} style={{
            border: 'none', background: t.inputBg || '#F1F5F9', borderRadius: 6, width: 28, height: 28,
            cursor: 'pointer', fontSize: 14, color: t.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>&times;</button>
        </div>

        {/* Progress stats */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: mono, color: green }}>{reviewed}</div>
            <div style={{ fontSize: 9, color: t.textFaint, textTransform: 'uppercase' }}>Reviewed</div>
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: mono, color: teal }}>{done}</div>
            <div style={{ fontSize: 9, color: t.textFaint, textTransform: 'uppercase' }}>Prepared</div>
          </div>
          {blocked > 0 && (
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: mono, color: purple }}>{blocked}</div>
              <div style={{ fontSize: 9, color: t.textFaint, textTransform: 'uppercase' }}>Blocked</div>
            </div>
          )}
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: mono, color: t.textSecondary }}>{total}</div>
            <div style={{ fontSize: 9, color: t.textFaint, textTransform: 'uppercase' }}>Total</div>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ height: 4, background: '#E2E8F0', borderRadius: 2, overflow: 'hidden', display: 'flex' }}>
          <div style={{ width: `${(reviewed / total) * 100}%`, background: green, transition: 'width 0.3s' }} />
          <div style={{ width: `${((done - reviewed) / total) * 100}%`, background: teal, transition: 'width 0.3s' }} />
          <div style={{ width: `${(blocked / total) * 100}%`, background: purple, transition: 'width 0.3s' }} />
        </div>

        {/* Column headers */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '24px 1fr 60px 32px 32px',
          alignItems: 'center',
          padding: '8px 12px 0',
          gap: 8,
          marginTop: 12,
        }}>
          <span />
          <span style={{ fontSize: 9, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', letterSpacing: 0.5 }}>Task</span>
          <span style={{ fontSize: 9, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'right' }}>Due</span>
          <span style={{ fontSize: 9, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'center' }}>P</span>
          <span style={{ fontSize: 9, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'center' }}>R</span>
        </div>
      </div>

      {/* Task list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
        {subSections.map(sub => {
          const subTasks = tasks.filter(t => t.section === sub);
          const cleanLabel = sub.includes(' - ') ? sub.split(' - ').pop().trim() : sub.includes('—') ? sub.split('—').pop().trim() : sub;
          return (
            <div key={sub}>
              {subSections.length > 1 && (
                <div style={{
                  fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: 0.5, color: teal, fontFamily: sans,
                  padding: '10px 4px 6px',
                  borderBottom: `2px solid ${teal}20`,
                  marginBottom: 6,
                }}>
                  {cleanLabel}
                </div>
              )}
              {subTasks.map(task => (
                <TaskItem
                  key={task.id}
                  task={task}
                  onToggleComplete={onToggleComplete}
                  onToggleReview={onToggleReview}
                  onToggleBlocked={onToggleBlocked}
                  onUpdateDeadline={onUpdateDeadline}
                  mockToday={mockToday}
                  userInitials={userInitials}
                  t={t}
                  expanded={expandedTask === task.id}
                  onToggleExpand={() => setExpandedTask(expandedTask === task.id ? null : task.id)}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
